import React from "react";
import PropTypes from "prop-types";
import styles from "./CustomerActivityGraph.module.css";
import { ChevronDown } from "react-ikonate";

import {
  AreaChart,
  Area,
  XAxis,
  ResponsiveContainer,
  ReferenceDot,
  ReferenceLine,
} from "recharts";

const DAY_LABELS = {
  Mon: "Monday",
  Tue: "Tuesday",
  Wed: "Wednesday",
  Thu: "Thursday",
  Fri: "Friday",
  Sat: "Saturday",
  Sun: "Sunday",
};

export function CustomerActivityGraph({
  data = [],
  activeDay = "Thu",
  title = "Customer Activity",
}) {
  const [selectedDay, setSelectedDay] = React.useState(activeDay);
  const [menuOpen, setMenuOpen] = React.useState(false);
  const dropdownRef = React.useRef(null);

  // Sync the highlighted day if the active day changes from outside
  // (e.g. a different product is selected on the Single Item tab).
  React.useEffect(() => {
    setSelectedDay(activeDay);
  }, [activeDay]);

  // Close the day menu on outside click or Escape.
  React.useEffect(() => {
    if (!menuOpen) return undefined;
    const handlePointer = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setMenuOpen(false);
      }
    };
    const handleKey = (event) => {
      if (event.key === "Escape") setMenuOpen(false);
    };
    document.addEventListener("mousedown", handlePointer);
    document.addEventListener("keydown", handleKey);
    return () => {
      document.removeEventListener("mousedown", handlePointer);
      document.removeEventListener("keydown", handleKey);
    };
  }, [menuOpen]);

  const maxValue = data.length ? Math.max(...data.map((d) => d.value)) : 0;
  const firstDay = data[0]?.day;
  const lastDay = data[data.length - 1]?.day;
  const dropdownLabel = DAY_LABELS[selectedDay] || selectedDay;

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <span className={styles.title}>{title}</span>
        <div className={styles.dropdownWrap} ref={dropdownRef}>
          <button
            type="button"
            className={styles.dropdown}
            onClick={() => setMenuOpen((open) => !open)}
            aria-haspopup="listbox"
            aria-expanded={menuOpen}
            title="Choose which day's activity to display."
          >
            {dropdownLabel}
            <ChevronDown size={18} className={styles.caret} />
          </button>

          {menuOpen && (
            <ul className={styles.dropdownMenu} role="listbox">
              {data.map((d) => (
                <li key={d.day}>
                  <button
                    type="button"
                    role="option"
                    aria-selected={d.day === selectedDay}
                    className={
                      d.day === selectedDay
                        ? `${styles.dropdownItem} ${styles.dropdownItemActive}`
                        : styles.dropdownItem
                    }
                    onClick={() => {
                      setSelectedDay(d.day);
                      setMenuOpen(false);
                    }}
                    title={`Show activity for ${DAY_LABELS[d.day] || d.day}.`}
                  >
                    {DAY_LABELS[d.day] || d.day}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>

      <div className={styles.chartWrapper}>
        <ResponsiveContainer width="100%" height="100%">
          <AreaChart
            data={data}
            margin={{ top: 20, right: 0, left: 0, bottom: 24 }}
          >
            <defs>
              <linearGradient id="activityGradient" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor="#243746" stopOpacity={0.35} />
                <stop offset="100%" stopColor="#ffffff" stopOpacity={0} />
              </linearGradient>
            </defs>

            <XAxis
              dataKey="day"
              axisLine={false}
              tickLine={false}
              interval={0}
              padding={{ left: 0, right: 0 }}
              tick={({ x, y, payload }) => {
                const value = payload.value;
                const isActive = value === selectedDay;
                const isFirst = value === firstDay;
                const isLast = value === lastDay;
                const tx = isFirst ? x + 20 : isLast ? x - 20 : x;

                return (
                  <text
                    x={tx}
                    y={y + 10}
                    textAnchor="middle"
                    className={styles.xTick}
                    fill={isActive ? "#91B6BB" : "#2E2A2A"}
                    fontWeight={isActive ? 600 : 400}
                  >
                    {value}
                  </text>
                );
              }}
            />

            <ReferenceLine
              segment={[
                { x: selectedDay, y: maxValue * 0.08 },
                { x: selectedDay, y: maxValue },
              ]}
              stroke="#D9DEE3"
              strokeWidth={14}
              strokeLinecap="round"
            />

            <Area
              type="monotone"
              dataKey="value"
              stroke="#243746"
              fill="url(#activityGradient)"
              strokeWidth={2}
              dot={false}
            />

            {data.map((d) =>
              d.day === selectedDay ? (
                <ReferenceDot
                  key={d.day}
                  x={d.day}
                  y={d.value}
                  r={7}
                  fill="#91B6BB"
                  stroke="#ffffff"
                  strokeWidth={3}
                />
              ) : null
            )}
          </AreaChart>
        </ResponsiveContainer>
      </div>
    </div>
  );
}

CustomerActivityGraph.propTypes = {
  data: PropTypes.arrayOf(
    PropTypes.shape({
      day: PropTypes.string.isRequired,
      value: PropTypes.number.isRequired,
    })
  ).isRequired,
  activeDay: PropTypes.string,
  title: PropTypes.string,
};
